import { Layers, Sigma } from "lucide-react";
import { useMemo, useRef } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { compactDecimalsFor, decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateSheetThickness, calculateSheetThicknessForAMass, sheetThicknessPresets } from "./formulas";
import type { SheetThicknessInputs } from "./types";

type SheetThicknessCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  inputs: SheetThicknessInputs;
  inputText: Record<keyof SheetThicknessInputs, string>;
  onInputTextChange: (key: keyof SheetThicknessInputs, value: string) => void;
};

export function SheetThicknessCalculator({
  language,
  unitSystem,
  inputs,
  inputText,
  onInputTextChange
}: SheetThicknessCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const matrixScrollRef = useRef<HTMLDivElement | null>(null);
  const result = useMemo(() => calculateSheetThickness(inputs), [inputs]);
  const matrix = useMemo(
    () => sheetThicknessPresets.map((aMass) => ({ aMass, result: calculateSheetThicknessForAMass(aMass) })),
    []
  );
  const minDisplay = toDisplayValue("aMass", result.recommendedMin, unitSystem);
  const maxDisplay = toDisplayValue("aMass", result.recommendedMax, unitSystem);
  const thicknessDecimals = unitSystem === "metric" ? 0 : 3;
  const selectedPreset = sheetThicknessPresets.find((preset) => Math.abs(preset - inputs.aMass) < 0.001);

  function selectPreset(aMass: number) {
    const displayValue = toDisplayValue("aMass", aMass, unitSystem);
    onInputTextChange("aMass", String(Number(displayValue.toFixed(unitSystem === "metric" ? 2 : 3))));
    requestAnimationFrame(() => centerMatrixColumn(aMass));
  }

  function centerMatrixColumn(aMass: number) {
    const scrollElement = matrixScrollRef.current;
    const columnElement = scrollElement?.querySelector<HTMLElement>(`[data-a-mass="${aMass}"]`);
    const rowLabelElement = scrollElement?.querySelector<HTMLElement>("thead th:first-child");
    if (!scrollElement || !columnElement) return;

    const stickyLabelWidth = rowLabelElement?.offsetWidth ?? 0;
    const nextLeft =
      columnElement.offsetLeft +
      columnElement.offsetWidth / 2 -
      scrollElement.clientWidth / 2 -
      stickyLabelWidth / 2;

    scrollElement.scrollTo({
      left: Math.max(0, nextLeft),
      behavior: "smooth"
    });
  }

  return (
    <section className="calculator-panel sheet-thickness-panel" aria-label={t.modules.sheetThickness}>
      <div className="calculator-heading">
        <div>
          <h2>{t.modules.sheetThickness}</h2>
          <span>{t.modules.sheetThicknessDescription}</span>
        </div>
      </div>

      <div className="calculator-body conversion-body sheet-thickness-body">
        <div className="parameter-section">
          <div className="section-label">{t.inputParameters}</div>
          <div className="input-grid">
            <NumericInput
              label={t.sheetThickness.aMass}
              shortLabel="a"
              unit={unitFor("aMass", unitSystem)}
              value={toDisplayValue("aMass", inputs.aMass, unitSystem)}
              textValue={inputText.aMass}
              decimals={decimalsFor("aMass", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("aMass", value)}
            />
          </div>

          <div className="preset-panel">
            <div className="section-label">{t.sheetThickness.presetSelection}</div>
            <div className="preset-grid">
              {sheetThicknessPresets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={selectedPreset === preset ? "preset-button is-active" : "preset-button"}
                  onClick={() => selectPreset(preset)}
                >
                  {formatNumber(toDisplayValue("aMass", preset, unitSystem), unitSystem === "metric" ? 0 : 3, locale)}
                </button>
              ))}
            </div>
          </div>

          <div className="sheet-matrix">
            <div className="section-label">{t.sheetThickness.matrix}</div>
            <div ref={matrixScrollRef} className="matrix-scroll" role="region" aria-label={t.sheetThickness.matrix}>
              <table>
                <thead>
                  <tr>
                    <th>{t.sheetThickness.aMass}</th>
                    {matrix.map((entry) => (
                      <th
                        key={entry.aMass}
                        data-a-mass={entry.aMass}
                        className={selectedPreset === entry.aMass ? "is-active" : ""}
                      >
                        {formatNumber(toDisplayValue("aMass", entry.aMass, unitSystem), unitSystem === "metric" ? 0 : 3, locale)}
                      </th>
                    ))}
                    <th className="matrix-scroll-spacer" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  <tr className="matrix-section-start">
                    <th>{t.sheetThickness.froniusMin}</th>
                    {matrix.map((entry) => (
                      <td key={entry.aMass} className={selectedPreset === entry.aMass ? "is-active" : ""}>
                        {formatNumber(
                          toDisplayValue("aMass", entry.result.recommendedMin, unitSystem),
                          thicknessDecimals,
                          locale
                        )}
                      </td>
                    ))}
                    <td className="matrix-scroll-spacer" aria-hidden="true" />
                  </tr>
                  <tr>
                    <th>{t.sheetThickness.froniusMax}</th>
                    {matrix.map((entry) => (
                      <td key={entry.aMass} className={selectedPreset === entry.aMass ? "is-active" : ""}>
                        {formatNumber(
                          toDisplayValue("aMass", entry.result.recommendedMax, unitSystem),
                          thicknessDecimals,
                          locale
                        )}
                      </td>
                    ))}
                    <td className="matrix-scroll-spacer" aria-hidden="true" />
                  </tr>
                  <tr className="matrix-section-start">
                    <th>{t.sheetThickness.normMin}</th>
                    {matrix.map((entry) => (
                      <td key={entry.aMass} className={selectedPreset === entry.aMass ? "is-active" : ""}>
                        {formatNumber(
                          toDisplayValue("aMass", entry.result.normMin, unitSystem),
                          compactDecimalsFor("aMass", unitSystem, toDisplayValue("aMass", entry.result.normMin, unitSystem)),
                          locale
                        )}
                      </td>
                    ))}
                    <td className="matrix-scroll-spacer" aria-hidden="true" />
                  </tr>
                  <tr>
                    <th>{t.sheetThickness.normMax}</th>
                    {matrix.map((entry) => (
                      <td key={entry.aMass} className={selectedPreset === entry.aMass ? "is-active" : ""}>
                        {formatNumber(
                          toDisplayValue("aMass", entry.result.normMax, unitSystem),
                          compactDecimalsFor("aMass", unitSystem, toDisplayValue("aMass", entry.result.normMax, unitSystem)),
                          locale
                        )}
                      </td>
                    ))}
                    <td className="matrix-scroll-spacer" aria-hidden="true" />
                  </tr>
                  <tr className="matrix-section-start">
                    <th>{t.sheetThickness.julesMin}</th>
                    {matrix.map((entry) => (
                      <td key={entry.aMass} className={selectedPreset === entry.aMass ? "is-active" : ""}>
                        {formatNumber(
                          toDisplayValue("aMass", entry.result.julesMin, unitSystem),
                          compactDecimalsFor("aMass", unitSystem, toDisplayValue("aMass", entry.result.julesMin, unitSystem)),
                          locale
                        )}
                      </td>
                    ))}
                    <td className="matrix-scroll-spacer" aria-hidden="true" />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <aside className="result-panel conversion-result" aria-live="polite">
          <div className="result-header">
            <span>
              <Layers size={16} /> {t.result}
            </span>
          </div>
          <p className="result-label">{t.sheetThickness.recommendation}</p>
          <div className="result-value result-range">
            {formatNumber(minDisplay, thicknessDecimals, locale)}
            <span>{unitFor("aMass", unitSystem)}</span>
            <b>-</b>
            {formatNumber(maxDisplay, thicknessDecimals, locale)}
            <span>{unitFor("aMass", unitSystem)}</span>
          </div>

          <div className="formula-box">
            <span>
              <Sigma size={15} /> {t.formula}
            </span>
            <code>min = floor((a / 0.7) * 0.83), max = ceil(a^2 + 0.5)</code>
          </div>

          <div className="detail-grid sheet-result-grid">
            <div className="result-section-start">
              <span>{t.sheetThickness.froniusMin}</span>
              <strong>
                {formatNumber(minDisplay, thicknessDecimals, locale)} {unitFor("aMass", unitSystem)}
              </strong>
            </div>
            <div>
              <span>{t.sheetThickness.froniusMax}</span>
              <strong>
                {formatNumber(maxDisplay, thicknessDecimals, locale)} {unitFor("aMass", unitSystem)}
              </strong>
            </div>
            <div className="result-section-start">
              <span>{t.sheetThickness.normMin}</span>
              <strong>
                {formatNumber(
                  toDisplayValue("aMass", result.normMin, unitSystem),
                  compactDecimalsFor("aMass", unitSystem, toDisplayValue("aMass", result.normMin, unitSystem)),
                  locale
                )}{" "}
                {unitFor("aMass", unitSystem)}
              </strong>
            </div>
            <div>
              <span>{t.sheetThickness.normMax}</span>
              <strong>
                {formatNumber(
                  toDisplayValue("aMass", result.normMax, unitSystem),
                  compactDecimalsFor("aMass", unitSystem, toDisplayValue("aMass", result.normMax, unitSystem)),
                  locale
                )}{" "}
                {unitFor("aMass", unitSystem)}
              </strong>
            </div>
            <div className="result-section-start">
              <span>{t.sheetThickness.julesMin}</span>
              <strong>
                {formatNumber(
                  toDisplayValue("aMass", result.julesMin, unitSystem),
                  compactDecimalsFor("aMass", unitSystem, toDisplayValue("aMass", result.julesMin, unitSystem)),
                  locale
                )}{" "}
                {unitFor("aMass", unitSystem)}
              </strong>
            </div>
            <div>
              <span>{t.sheetThickness.julesFactor}</span>
              <strong>{formatNumber(result.julesFactor, 2, locale)}</strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
